import React, { useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { toast } from 'sonner';
import { Upload, FileArchive, AlertTriangle, Loader2, CheckCircle2, RotateCcw } from 'lucide-react';

/**
 * CompanyRestoreDataDialog — restore a company from a ZIP export
 * (data.json + images/). Two-step, non-destructive-by-default flow:
 *
 *   1. Pick a .zip → we call /restore/analyze to show how many records are
 *      NEW vs already EXISTING in this company (nothing is written yet).
 *   2. Choose a single policy — Skip existing (safe, default) or Overwrite
 *      existing — then Apply.
 *
 * A prominent warning appears when the backup's source company differs from
 * the target, since that would clone another company's data in here.
 */
export function CompanyRestoreDataDialog({ company, open, onOpenChange, onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [policy, setPolicy] = useState('skip');

  const reset = () => {
    setFile(null); setAnalysis(null); setPolicy('skip');
    setAnalyzing(false); setApplying(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = (v) => {
    if (analyzing || applying) return; // don't close mid-operation
    if (!v) reset();
    onOpenChange(v);
  };

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setAnalysis(null);
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await api.post(`/admin/companies/${company.id}/restore/analyze`, fd);
      setAnalysis(data);
    } catch (err) {
      toast.error(extractError(err, 'Could not read this backup file'));
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } finally {
      setAnalyzing(false);
    }
  };

  const apply = async () => {
    if (!file) return;
    setApplying(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('policy', policy);
      const { data } = await api.post(`/admin/companies/${company.id}/restore/apply`, fd);
      const t = data.totals || {};
      toast.success(
        `Restore complete — ${t.inserted || 0} added, ${t.overwritten || 0} overwritten, ${t.skipped || 0} skipped.`,
      );
      reset();
      onOpenChange(false);
      onDone && onDone();
    } catch (err) {
      toast.error(extractError(err, 'Restore failed'));
    } finally {
      setApplying(false);
    }
  };

  const mismatch = analysis && analysis.source_company_id && analysis.source_company_id !== company?.id;
  const nonEmpty = (analysis?.summary || []).filter((s) => s.total > 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl" data-testid="company-restore-data-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw size={18} className="text-violet-600" /> Restore Data — {company?.name}
          </DialogTitle>
          <DialogDescription>
            Upload a ZIP previously created with <span className="font-medium">Export Data</span>.
            We'll re-create records and restore all images. Existing records are detected up front
            so you can choose to skip or overwrite them — nothing is duplicated.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1 — choose a file */}
        <div className="rounded-lg border border-dashed border-[var(--divider)] bg-[var(--paper-warm)] px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <FileArchive size={18} className="text-slate-500 shrink-0" />
            <span className="text-sm text-slate-700 truncate" data-testid="restore-data-filename">
              {file ? file.name : 'No file selected'}
            </span>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".zip,application/zip"
            hidden
            onChange={onPick}
            data-testid="restore-data-file-input"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={analyzing || applying}
            data-testid="restore-data-choose-file"
          >
            <Upload size={14} className="mr-1.5" /> {file ? 'Change file' : 'Choose ZIP'}
          </Button>
        </div>

        {analyzing && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-3" data-testid="restore-data-analyzing">
            <Loader2 size={15} className="animate-spin" /> Analysing backup…
          </div>
        )}

        {/* Step 2 — analysis summary + policy */}
        {analysis && !analyzing && (
          <div className="space-y-3" data-testid="restore-data-analysis">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge className="bg-slate-100 text-slate-700">From: {analysis.source_company_name || 'Unknown'}</Badge>
              <Badge className="bg-slate-100 text-slate-700">Exported: {analysis.exported_at ? String(analysis.exported_at).slice(0, 10) : '—'}</Badge>
              <Badge className="bg-emerald-50 text-emerald-700" data-testid="restore-data-total-new">{analysis.totals?.new || 0} new</Badge>
              <Badge className="bg-amber-50 text-amber-700" data-testid="restore-data-total-existing">{analysis.totals?.existing || 0} already exist</Badge>
              <Badge className="bg-slate-100 text-slate-700">{analysis.totals?.total || 0} total</Badge>
              {analysis.image_count ? <Badge className="bg-violet-50 text-violet-700">{analysis.image_count} images</Badge> : null}
            </div>

            {mismatch && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800" data-testid="restore-data-mismatch-warning">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                <span>
                  This backup was created from <b>{analysis.source_company_name}</b>, which is a different
                  company. Restoring here will copy that company's records into <b>{company?.name}</b>.
                </span>
              </div>
            )}

            <div className="max-h-52 overflow-y-auto rounded-md border border-[var(--divider)]">
              <table className="w-full text-[12.5px]">
                <thead className="bg-slate-50 text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-medium">Collection</th>
                    <th className="text-right px-3 py-1.5 font-medium">Total</th>
                    <th className="text-right px-3 py-1.5 font-medium text-emerald-700">New</th>
                    <th className="text-right px-3 py-1.5 font-medium text-amber-700">Exists</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--divider)]">
                  {nonEmpty.map((s) => (
                    <tr key={s.collection} data-testid={`restore-data-row-${s.collection}`}>
                      <td className="px-3 py-1.5 text-slate-700">{s.collection}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums">{s.total}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums text-emerald-700">{s.new}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums text-amber-700">{s.existing}</td>
                    </tr>
                  ))}
                  {nonEmpty.length === 0 && (
                    <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-400">Backup contains no records.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div>
              <Label className="text-[12px] text-slate-600 mb-1.5 block">When a record already exists:</Label>
              <RadioGroup value={policy} onValueChange={setPolicy} className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="restore-data-policy">
                <label className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer ${policy === 'skip' ? 'border-violet-400 bg-violet-50' : 'border-[var(--divider)] hover:bg-slate-50'}`}>
                  <RadioGroupItem value="skip" id="policy-skip" data-testid="restore-data-policy-skip" className="mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-slate-800">Skip existing</div>
                    <div className="text-[11px] text-slate-500">Only add records that don't exist yet. Safest.</div>
                  </div>
                </label>
                <label className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer ${policy === 'overwrite' ? 'border-violet-400 bg-violet-50' : 'border-[var(--divider)] hover:bg-slate-50'}`}>
                  <RadioGroupItem value="overwrite" id="policy-overwrite" data-testid="restore-data-policy-overwrite" className="mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-slate-800">Overwrite existing</div>
                    <div className="text-[11px] text-slate-500">Replace matching records with the backup version.</div>
                  </div>
                </label>
              </RadioGroup>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleClose(false)} disabled={analyzing || applying} data-testid="restore-data-cancel">
            Cancel
          </Button>
          <Button
            onClick={apply}
            disabled={!analysis || analyzing || applying}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="restore-data-apply"
          >
            {applying ? (<><Loader2 size={14} className="mr-1.5 animate-spin" /> Restoring…</>)
              : (<><CheckCircle2 size={14} className="mr-1.5" /> Restore {policy === 'overwrite' ? '(Overwrite)' : '(Skip existing)'}</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
